import React, { useState, useEffect } from 'react';
import { Target, TrendingUp, TrendingDown, Flame, Calendar, Clock, Lightbulb, ChevronRight, Sparkles } from 'lucide-react';
import { UserProfile } from '../types';

interface DailyStats {
  date: string;
  count: number;
  likhit: number;
  smaran: number;
  vani: number;
}

interface SadhanaInsight {
  suggestedGoal: number;
  averageDaily: number;
  bestDay: string;
  bestDayCount: number;
  weakDay: string;
  trend: 'up' | 'down' | 'stable';
  trendPercentage: number;
  currentStreak: number;
  bestStreak: number;
  bestTime: string;
  motivationalMessage: string;
  tip: string;
}

const DAYS_HI = ['रविवार', 'सोमवार', 'मंगलवार', 'बुधवार', 'गुरुवार', 'शुक्रवार', 'शनिवार'];
const DAYS_EN = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const MOTIVATIONAL_MESSAGES = [
  { condition: 'streak_high', message: '🔥 अद्भुत! आपकी साधना में निरंतरता प्रशंसनीय है!' },
  { condition: 'streak_start', message: '🌟 शुभारंभ! निरंतरता से सिद्धि मिलती है।' },
  { condition: 'above_average', message: '📈 बहुत बढ़िया! आप औसत से आगे हैं!' },
  { condition: 'below_average', message: '💪 हर कदम मायने रखता है। आज फिर प्रयास करें!' },
  { condition: 'new_user', message: '🙏 स्वागत है! राम नाम की यात्रा शुरू करें।' },
];

const TIPS = [
  'प्रातःकाल 4-6 बजे का समय जाप के लिए सर्वोत्तम है।',
  'जाप से पहले 3 गहरी साँसें लें।',
  'एक निश्चित स्थान पर बैठकर जाप करें।',
  'मन भटके तो धीरे से वापस लाएं, निराश न हों।',
  '108 की एक माला पूरी करने का लक्ष्य रखें।',
  'जाप के बाद कुछ क्षण मौन रहें।',
  'सप्ताह में एक दिन विशेष जाप संकल्प लें।',
];

export const PersonalizedDashboard: React.FC<{ user: UserProfile }> = ({ user }) => {
  const [insights, setInsights] = useState<SadhanaInsight | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  
  useEffect(() => {
    calculateInsights();
  }, [user]);
  
  const calculateInsights = () => {
    // Get daily stats from localStorage
    const dailyStatsKey = `jaap_daily_stats_${user.id}`;
    const storedStats: DailyStats[] = JSON.parse(localStorage.getItem(dailyStatsKey) || '[]');
    
    // If no history, create from current data
    if (storedStats.length === 0 && user.total_count > 0) {
      const today = new Date().toISOString().split('T')[0];
      storedStats.push({
        date: today,
        count: user.total_count,
        likhit: (Object.values(user.likhit_counts || {}) as number[]).reduce((a, b) => a + b, 0),
        smaran: (Object.values(user.smaran_counts || {}) as number[]).reduce((a, b) => a + b, 0),
        vani: (Object.values(user.vani_counts || {}) as number[]).reduce((a, b) => a + b, 0),
      });
    }
    
    // Calculate insights
    const last7Days = storedStats.slice(-7);
    const last14Days = storedStats.slice(-14);
    
    // Average daily count
    const averageDaily = last7Days.length > 0 
      ? Math.round(last7Days.reduce((sum, d) => sum + d.count, 0) / last7Days.length)
      : 0;
    
    // Find best and weak days
    const dayTotals: Record<number, number[]> = {};
    storedStats.forEach(stat => {
      const dayOfWeek = new Date(stat.date).getDay();
      if (!dayTotals[dayOfWeek]) dayTotals[dayOfWeek] = [];
      dayTotals[dayOfWeek].push(stat.count);
    });
    
    let bestDayIndex = 0, bestDayAvg = 0;
    let weakDayIndex = 0, weakDayAvg = Infinity;
    
    Object.entries(dayTotals).forEach(([day, counts]) => {
      const avg = counts.reduce((a, b) => a + b, 0) / counts.length;
      if (avg > bestDayAvg) { bestDayAvg = avg; bestDayIndex = parseInt(day); }
      if (avg < weakDayAvg) { weakDayAvg = avg; weakDayIndex = parseInt(day); }
    });
    
    // Calculate trend (compare last 7 days with previous 7 days)
    const recentAvg = last7Days.length > 0 
      ? last7Days.reduce((sum, d) => sum + d.count, 0) / last7Days.length 
      : 0;
    const previousAvg = last14Days.slice(0, 7).length > 0 
      ? last14Days.slice(0, 7).reduce((sum, d) => sum + d.count, 0) / last14Days.slice(0, 7).length 
      : recentAvg;
    
    const trendPercentage = previousAvg > 0 
      ? Math.round(((recentAvg - previousAvg) / previousAvg) * 100) 
      : 0;
    const trend = trendPercentage > 5 ? 'up' : trendPercentage < -5 ? 'down' : 'stable';
    
    // Suggested goal (10-20% above average, rounded to nice number)
    let suggestedGoal = Math.round(averageDaily * 1.15);
    if (suggestedGoal < 108) suggestedGoal = 108; // Minimum one mala
    suggestedGoal = Math.ceil(suggestedGoal / 10) * 10; // Round to nearest 10
    
    // Best time (mock - could be enhanced with actual time tracking)
    const bestTime = '5:00 - 7:00 AM';
    
    // Get motivational message
    let motivationalMessage = MOTIVATIONAL_MESSAGES[4].message; // Default new user
    if (user.streak_days >= 7) {
      motivationalMessage = MOTIVATIONAL_MESSAGES[0].message;
    } else if (user.streak_days >= 1) {
      motivationalMessage = MOTIVATIONAL_MESSAGES[1].message;
    } else if (recentAvg > averageDaily) {
      motivationalMessage = MOTIVATIONAL_MESSAGES[2].message;
    } else if (storedStats.length > 0) {
      motivationalMessage = MOTIVATIONAL_MESSAGES[3].message;
    }
    
    // Random tip
    const tip = TIPS[Math.floor(Math.random() * TIPS.length)];
    
    // Best streak from localStorage
    const bestStreak = parseInt(localStorage.getItem(`best_streak_${user.id}`) || '0') || user.streak_days;
    
    setInsights({
      suggestedGoal,
      averageDaily,
      bestDay: DAYS_HI[bestDayIndex],
      bestDayCount: Math.round(bestDayAvg),
      weakDay: DAYS_HI[weakDayIndex],
      trend,
      trendPercentage: Math.abs(trendPercentage),
      currentStreak: user.streak_days,
      bestStreak: Math.max(bestStreak, user.streak_days),
      bestTime,
      motivationalMessage,
      tip,
    });
    
    // Update best streak if current is higher
    if (user.streak_days > bestStreak) {
      localStorage.setItem(`best_streak_${user.id}`, user.streak_days.toString());
    }
  };
  
  if (!insights) return null;
  
  return (
    <div className="space-y-4" data-testid="personalized-dashboard">
      {/* Today's Goal Card */}
      <div className="bg-gradient-to-br from-orange-500 to-amber-500 rounded-2xl p-5 text-white shadow-lg">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-white/80 text-sm mb-1">आज का लक्ष्य</p>
            <div className="flex items-baseline gap-2">
              <span className="text-4xl font-bold">{insights.suggestedGoal.toLocaleString()}</span>
              <span className="text-white/80">राम नाम</span>
            </div>
          </div>
          <div className="bg-white/20 p-3 rounded-xl">
            <Target size={28} />
          </div>
        </div>
        
        <div className="mt-4 pt-4 border-t border-white/20">
          <p className="text-sm text-white/90">{insights.motivationalMessage}</p>
        </div>
      </div>
      
      {/* Quick Stats */}
      <div className="grid grid-cols-2 gap-3">
        {/* Average */}
        <div className="bg-white dark:bg-stone-800 rounded-xl p-4 border border-gray-100 dark:border-stone-700">
          <div className="flex items-center gap-2 mb-2">
            <div className="p-1.5 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              {insights.trend === 'up' ? (
                <TrendingUp size={16} className="text-blue-500" />
              ) : insights.trend === 'down' ? (
                <TrendingDown size={16} className="text-red-500" />
              ) : (
                <Target size={16} className="text-blue-500" />
              )}
            </div>
            <span className="text-xs text-gray-500 dark:text-gray-400">7-दिन औसत</span>
          </div>
          <p className="text-xl font-bold text-gray-800 dark:text-white">
            {insights.averageDaily.toLocaleString()}
          </p>
          {insights.trend !== 'stable' && (
            <p className={`text-xs ${insights.trend === 'up' ? 'text-green-500' : 'text-red-500'}`}>
              {insights.trend === 'up' ? '↑' : '↓'} {insights.trendPercentage}% पिछले हफ्ते से
            </p>
          )}
        </div>
        
        {/* Streak */}
        <div className="bg-white dark:bg-stone-800 rounded-xl p-4 border border-gray-100 dark:border-stone-700">
          <div className="flex items-center gap-2 mb-2">
            <div className="p-1.5 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
              <Flame size={16} className="text-orange-500" />
            </div>
            <span className="text-xs text-gray-500 dark:text-gray-400">स्ट्रीक</span>
          </div>
          <p className="text-xl font-bold text-gray-800 dark:text-white">
            {insights.currentStreak} दिन
          </p>
          <p className="text-xs text-gray-400">
            सर्वश्रेष्ठ: {insights.bestStreak} दिन
          </p>
        </div>
      </div>
      
      {/* Insights Button */}
      <button
        onClick={() => setShowDetails(!showDetails)}
        className="w-full bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 rounded-xl p-4 flex items-center justify-between hover:shadow-md transition-shadow"
        data-testid="insights-toggle"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-xl">
            <Sparkles size={20} className="text-purple-500" />
          </div>
          <div className="text-left">
            <p className="font-medium text-gray-800 dark:text-white">साधना अंतर्दृष्टि</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">व्यक्तिगत विश्लेषण देखें</p>
          </div>
        </div>
        <ChevronRight size={20} className={`text-gray-400 transition-transform ${showDetails ? 'rotate-90' : ''}`} />
      </button>
      
      {/* Detailed Insights */}
      {showDetails && (
        <div className="bg-white dark:bg-stone-800 rounded-xl p-4 space-y-4 border border-gray-100 dark:border-stone-700 animate-fade-in">
          <h4 className="font-semibold text-gray-800 dark:text-white flex items-center gap-2">
            <Lightbulb size={18} className="text-yellow-500" /> विस्तृत विश्लेषण
          </h4>
          
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="bg-gray-50 dark:bg-stone-700/50 rounded-lg p-3">
              <p className="text-gray-500 dark:text-gray-400 text-xs mb-1">सर्वश्रेष्ठ दिन</p>
              <p className="font-medium text-gray-800 dark:text-white">{insights.bestDay}</p>
              <p className="text-xs text-green-500">~{insights.bestDayCount} जाप</p>
            </div>
            
            <div className="bg-gray-50 dark:bg-stone-700/50 rounded-lg p-3">
              <p className="text-gray-500 dark:text-gray-400 text-xs mb-1">सुधार की गुंजाइश</p>
              <p className="font-medium text-gray-800 dark:text-white">{insights.weakDay}</p>
              <p className="text-xs text-orange-500">रिमाइंडर लगाएं</p>
            </div>
            
            <div className="bg-gray-50 dark:bg-stone-700/50 rounded-lg p-3">
              <p className="text-gray-500 dark:text-gray-400 text-xs mb-1">सर्वोत्तम समय</p>
              <p className="font-medium text-gray-800 dark:text-white flex items-center gap-1">
                <Clock size={14} /> {insights.bestTime}
              </p>
            </div>
            
            <div className="bg-gray-50 dark:bg-stone-700/50 rounded-lg p-3">
              <p className="text-gray-500 dark:text-gray-400 text-xs mb-1">इस माह प्रगति</p>
              <p className="font-medium text-gray-800 dark:text-white">
                {insights.trend === 'up' ? '📈 बढ़त' : insights.trend === 'down' ? '📉 कमी' : '➡️ स्थिर'}
              </p>
            </div>
          </div>
          
          {/* Daily Tip */}
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-lg p-3 border border-amber-200 dark:border-amber-800/30">
            <p className="text-xs text-amber-600 dark:text-amber-400 font-medium mb-1">💡 आज का सुझाव</p>
            <p className="text-sm text-gray-700 dark:text-gray-300">{insights.tip}</p>
          </div>
        </div>
      )}
    </div>
  );
};

// Utility function to save daily stats (call this when user does Jaap)
export const saveDailyStats = (userId: string, stats: DailyStats) => {
  const key = `jaap_daily_stats_${userId}`;
  const existing: DailyStats[] = JSON.parse(localStorage.getItem(key) || '[]');
  
  // Update today's stats or add new
  const today = new Date().toISOString().split('T')[0];
  const todayIndex = existing.findIndex(s => s.date === today);
  
  if (todayIndex >= 0) {
    existing[todayIndex] = stats;
  } else {
    existing.push(stats);
  }
  
  // Keep only last 90 days
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - 90);
  const filtered = existing.filter(s => new Date(s.date) >= cutoff);
  
  localStorage.setItem(key, JSON.stringify(filtered));
};

export default PersonalizedDashboard;
